import type { CurrencyCode, IsoDateTime, Money, PostalAddress } from './common';
import type { CredentialId, ProductId, TenantId } from './ids';
import type { NormalizedFinanceRow } from './profit';
import type { CarrierCode, LabelFormat, PaperSize, ShippingLabel, ShippingModel } from './shipping';

export const MARKETPLACE_CHANNELS = [
  'trendyol',
  'hepsiburada',
  'amazon',
  'shopify',
  'n11',
  'ciceksepeti',
  'pttavm',
  'pazarama',
  'idefix',
  'koctas',
  'teknosa',
  'manual',
] as const;
export type MarketplaceChannel = (typeof MARKETPLACE_CHANNELS)[number];

/** Channels reached through an adapter; manual orders are typed in by the seller. */
export type IntegratedChannel = Exclude<MarketplaceChannel, 'manual'>;

/** How a channel is named to people: its name, the letters on its badge, and the site its buyers shop on. */
export const CHANNEL_INFO: Readonly<Record<MarketplaceChannel, { readonly name: string; readonly short: string; readonly site: string }>> = {
  trendyol: { name: 'Trendyol', short: 'TY', site: 'trendyol.com' },
  hepsiburada: { name: 'Hepsiburada', short: 'HB', site: 'hepsiburada.com' },
  amazon: { name: 'Amazon', short: 'AZ', site: 'amazon.com.tr' },
  shopify: { name: 'Shopify', short: 'SH', site: 'shopify.com' },
  n11: { name: 'n11', short: 'n11', site: 'n11.com' },
  ciceksepeti: { name: 'Çiçeksepeti', short: 'ÇS', site: 'ciceksepeti.com' },
  pttavm: { name: 'PttAVM', short: 'PTT', site: 'pttavm.com' },
  pazarama: { name: 'Pazarama', short: 'PZ', site: 'pazarama.com' },
  idefix: { name: 'İdefix', short: 'İD', site: 'idefix.com' },
  koctas: { name: 'Koçtaş', short: 'KÇ', site: 'koctas.com.tr' },
  teknosa: { name: 'Teknosa', short: 'TK', site: 'teknosa.com' },
  manual: { name: 'Manuel', short: 'MN', site: '' },
};

/** "ciceksepeti" → "Çiçeksepeti"; a channel name not listed reads as itself. */
export const CHANNEL_NAMES: Readonly<Record<string, string>> = Object.fromEntries(MARKETPLACE_CHANNELS.map((c) => [c, CHANNEL_INFO[c].name]));

/**
 * Channels whose own contracted carriers usually take the parcel (the marketplace pays, and gives the
 * label or the cargo code), so a new account starts on the marketplace shipping model; the others start
 * on the seller's own contract.
 */
export const MARKETPLACE_SHIPPED_CHANNELS: readonly IntegratedChannel[] = ['trendyol', 'hepsiburada', 'n11', 'ciceksepeti', 'pttavm', 'pazarama', 'idefix'];

/**
 * Channels whose package turns "shipped" only when the carrier scans it: the warehouse never tells them
 * (their adapters skip the 'shipped' notice), so their "shipped" means the courier has the parcel. The
 * others hear "shipped" from the warehouse's own handover (PttAVM counts an order sent once its barcode
 * is made), so theirs is no proof the parcel left.
 */
export const CARRIER_SCAN_CHANNELS: readonly IntegratedChannel[] = ['trendyol', 'hepsiburada'];

/**
 * Channels whose single-order read asks the marketplace for that very order by its number (Hepsiburada: the order,
 * then its package; a number that is not the seller's answers 403 code 1005): an order not found there is one the
 * marketplace no longer knows for the seller (cancelled, given to another seller). Their adapters answer null only on
 * the marketplace's own word, never on an answer that says nothing (a bare 404 is an error). The others look an order
 * up in lists a date window or a status bounds, where an older order may simply not show.
 */
export const ORDER_BY_NUMBER_CHANNELS: readonly IntegratedChannel[] = ['hepsiburada'];

/**
 * Why an order waits on hold when its marketplace no longer knows it (the order sync puts it in front of any earlier
 * reason): Sorunlu siparişler shows it, the supervisor decides. Nothing of the order goes meanwhile. The acil iş
 * (migration 0073) knows the hold by its first words, "Pazaryerinde bulunamadı": keep them.
 */
export const CHANNEL_UNKNOWN_HOLD = 'Pazaryerinde bulunamadı: iptal edilmiş ya da başka satıcıya devredilmiş olabilir';

export const CREDENTIAL_STATUSES = ['active', 'disabled', 'auth_failed'] as const;
export type CredentialStatus = (typeof CREDENTIAL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Credentials
// ---------------------------------------------------------------------------

/**
 * Decrypted API secrets per channel. Persisted only as AES-256-GCM ciphertext
 * (marketplace_credentials.secret_*), decrypted in the worker right before a call, never logged.
 */
export interface ChannelSecrets {
  readonly trendyol: { readonly apiKey: string; readonly apiSecret: string };
  /**
   * username: the merchant id (or an integration user Hepsiburada gave); userAgent: the integrator name exactly as
   * Hepsiburada registered it, asked of every new account; accounts stored before send "<username> - SelfIntegration"
   */
  readonly hepsiburada: { readonly username: string; readonly password: string; readonly userAgent?: string };
  /** Login with Amazon refresh-token grant used by SP-API */
  readonly amazon: { readonly lwaClientId: string; readonly lwaClientSecret: string; readonly refreshToken: string };
  /** Admin API access token of the seller's custom app; its client secret signs webhooks */
  readonly shopify: { readonly accessToken: string; readonly webhookSecret?: string };
  /** Seller Office → Hesabım → API Hesapları: sent as the appkey / appsecret headers */
  readonly n11: { readonly appKey: string; readonly appSecret: string };
  /** Hesap Yönetimi → Entegrasyon Bilgilerim: the x-api-key header */
  readonly ciceksepeti: { readonly apiKey: string };
  /** the store's API key and the token made for its integrator (Api-Key / access-token headers) */
  readonly pttavm: { readonly apiKey: string; readonly accessToken: string };
  /** OAuth2 client credentials: API Key is the client id, API Secret the client secret */
  readonly pazarama: { readonly apiKey: string; readonly apiSecret: string };
  /** sent as X-API-KEY: base64("apiKey:apiSecret") */
  readonly idefix: { readonly apiKey: string; readonly apiSecret: string };
  /** Mirakl shop API key, sent raw in the Authorization header */
  readonly koctas: { readonly apiKey: string };
  readonly teknosa: { readonly apiKey: string };
}

/** Non-secret account settings per channel (marketplace_credentials.config). */
export interface ChannelConfig {
  /** storeFrontCode header, "TR" for trendyol.com */
  readonly trendyol: { readonly environment: 'production' | 'stage'; readonly storeFrontCode?: string };
  /** cancelReasonId: Hepsiburada's reason code used when a line cannot be supplied */
  readonly hepsiburada: { readonly environment: 'production' | 'test'; readonly cancelReasonId?: number };
  readonly amazon: { readonly region: 'eu' | 'na' | 'fe'; readonly marketplaceIds: readonly string[] };
  readonly shopify: { readonly apiVersion: string; readonly locationId: string | null };
  readonly n11: NoConfig;
  readonly ciceksepeti: { readonly environment: 'production' | 'test' };
  /** the PttAVM depot of the warehouse that PTT barcodes are made from; without it the only depot, or the one named "Pronto" */
  readonly pttavm: { readonly warehouseId?: number };
  readonly pazarama: NoConfig;
  /** stage needs the server's IP whitelisted by İdefix and has its own keys */
  readonly idefix: { readonly environment: 'production' | 'test' };
  readonly koctas: NoConfig;
  readonly teknosa: NoConfig;
}

/** A channel with no settings beside its keys. */
export type NoConfig = { readonly [key: string]: never };

/**
 * Ciphertext envelope as stored. The key never reaches PostgreSQL. The additional
 * authenticated data binds the ciphertext to its tenant and credential row, so a
 * ciphertext copied into another tenant's row fails to decrypt.
 */
export interface EncryptedSecret {
  readonly algorithm: 'aes-256-gcm';
  readonly keyId: string;
  /** 12 bytes */
  readonly iv: Uint8Array;
  /** 16 bytes */
  readonly authTag: Uint8Array;
  readonly ciphertext: Uint8Array;
}

export interface MarketplaceCredential<C extends IntegratedChannel = IntegratedChannel> {
  readonly id: CredentialId;
  readonly tenantId: TenantId;
  readonly channel: C;
  readonly label: string;
  /**
   * Trendyol sellerId · Hepsiburada merchantId · Amazon sellingPartnerId · Shopify shop domain · n11 store
   * name · Çiçeksepeti, PttAVM, Pazarama satıcı ID · İdefix vendorId · Koçtaş, Teknosa (Mirakl) shop_id
   */
  readonly externalAccountId: string;
  readonly config: ChannelConfig[C];
  readonly status: CredentialStatus;
  readonly defaultShippingModel: ShippingModel;
  readonly lastVerifiedAt: IsoDateTime | null;
  readonly lastError: string | null;
  /** Watermark of the incremental order pull */
  readonly ordersSyncedUntil: IsoDateTime | null;
  readonly createdAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
}

/** Discriminated by channel, so `config` narrows together with `channel`. */
export type AnyMarketplaceCredential = { [C in IntegratedChannel]: MarketplaceCredential<C> }[IntegratedChannel];

// ---------------------------------------------------------------------------
// NormalizedOrder: the single shape every adapter produces
// ---------------------------------------------------------------------------

export const CHANNEL_ORDER_STATES = [
  'awaiting_fulfillment',
  'in_fulfillment',
  'shipped',
  'delivered',
  'cancelled',
  'returned',
  'unknown',
] as const;
/**
 * Channel-side lifecycle mapped from each marketplace's vocabulary
 * (Trendyol Created → awaiting_fulfillment, Picking/Invoiced → in_fulfillment,
 * UnSupplied/Cancelled → cancelled, …). It decides whether an order is imported,
 * updated or cancelled in the WMS; it is not the WMS OrderStatus.
 */
export type ChannelOrderState = (typeof CHANNEL_ORDER_STATES)[number];

export interface ExternalOrderRef {
  readonly externalOrderId: string;
  /** Trendyol shipmentPackageId / Hepsiburada package number; null where the channel has no packages */
  readonly externalPackageId: string | null;
}

export interface NormalizedOrderLine {
  /** Line id the channel expects back in status updates */
  readonly externalLineId: string;
  /** Merchant SKU (stok kodu) */
  readonly sku: string | null;
  /** EAN/GTIN or the barcode registered on the channel listing */
  readonly barcode: string | null;
  /** ASIN, Hepsiburada SKU, Shopify variant id, … */
  readonly channelProductId: string | null;
  readonly name: string;
  readonly quantity: number;
  readonly unitPrice: Money;
  readonly discount: Money | null;
  /** KDV % */
  readonly vatRate: number | null;
  /** the marketplace's commission %, where the order says (Trendyol lines[].commission); kept for the Kârlılık estimate */
  readonly commissionRate?: number | null;
  /** the line's commission as the marketplace states it, KDV dahil (Hepsiburada commission.amount) */
  readonly commission?: Money | null;
}

/** Who the seller's e-Arşiv / e-Fatura is issued to (Modül C). */
export interface InvoiceParty {
  readonly kind: 'individual' | 'corporate';
  readonly name: string;
  /** TCKN (11) for individuals, VKN (10) for companies */
  readonly taxNumber: string | null;
  readonly taxOffice: string | null;
  readonly email: string | null;
  readonly address: PostalAddress;
}

export interface NormalizedShipping {
  readonly model: ShippingModel;
  readonly carrier: CarrierCode | null;
  /** e.g. Trendyol cargoTrackingNumber, known at import time for marketplace-contracted shipping */
  readonly trackingNumber: string | null;
  /** The channel can hand out a ready label (see AdapterCapabilities.providesShippingLabel) */
  readonly labelAvailable: boolean;
  readonly shipBy: IsoDateTime | null;
  /**
   * The channel says outright who carries this parcel (İdefix's cargo profile, Çiçeksepeti's cargo model,
   * n11's delivery kind), so `model` holds before any tracking number exists; otherwise a parcel with no
   * number yet takes the account's default model.
   */
  readonly modelStated?: boolean;
}

export interface NormalizedOrder {
  readonly channel: IntegratedChannel;
  readonly credentialId: CredentialId;
  readonly ref: ExternalOrderRef;
  /** Number shown to people; may differ from externalOrderId (Shopify "#1042") */
  readonly orderNumber: string;
  readonly state: ChannelOrderState;
  /** Raw channel status, kept for diagnostics */
  readonly channelStatus: string;
  readonly orderedAt: IsoDateTime;
  readonly updatedAt: IsoDateTime;
  readonly currency: CurrencyCode;
  readonly grandTotal: Money;
  readonly customer: { readonly name: string; readonly email: string | null; readonly phone: string | null };
  readonly shippingAddress: PostalAddress;
  readonly invoiceParty: InvoiceParty | null;
  readonly lines: readonly NormalizedOrderLine[];
  readonly shipping: NormalizedShipping;
  /**
   * The lines are only the ones the state is about, not the whole order: Hepsiburada lists cancelled line
   * items, not cancelled orders, so 'cancelled' cancels an order only when none of its other lines is left
   */
  readonly listedLinesOnly?: boolean;
  /**
   * Lines the channel cancelled while the rest of the order goes on, as a line's own status says (Trendyol's
   * orderLineItemStatusName, Pazarama's and PttAVM's line status, a Mirakl line CANCELED or REFUSED, a
   * Hepsiburada line item): `lines` holds only the rest. The warehouse takes them off an order it holds
   * (order_items.channel_cancelled_at); an order it does not hold yet comes without them
   */
  readonly cancelledLineIds?: readonly string[];
  /**
   * Packages of the same order this one was made of (Trendyol originPackageIds: a split, or a partial cancel
   * that packaged the rest anew). Their lines go on here: an order the warehouse holds under one of them is over,
   * whatever that package's own status still says (UnPacked, Repack) or whether it was read yet
   */
  readonly replacesPackageIds?: readonly string[];
  /** Original payload, archived for audits and re-processing; never shown in the seller portal */
  readonly raw: unknown;
}

/** Result of matching an order line to the catalog: channel listing → barcode → SKU. */
export type LineMatch =
  | { readonly matched: true; readonly productId: ProductId; readonly via: 'listing' | 'barcode' | 'sku' }
  | { readonly matched: false; readonly reason: 'unknown_product' | 'ambiguous' };

// ---------------------------------------------------------------------------
// Adapter contract (Strategy pattern): one implementation per channel
// ---------------------------------------------------------------------------

export interface AdapterCapabilities {
  /** The channel hands out a contracted-carrier label (Trendyol, Hepsiburada); otherwise Modül C buys one. */
  readonly providesShippingLabel: boolean;
  /** Lines must be moved to a picking state before they can be invoiced (Trendyol "Picking"). */
  readonly requiresPickingStatus: boolean;
  /** Status changes are addressed per shipment package, not per order. */
  readonly packageLevelFulfillment: boolean;
  /** The seller's invoice number/link can be posted back (Trendyol "Invoiced"). */
  readonly acceptsInvoiceReference: boolean;
  readonly supportsWebhooks: boolean;
  readonly supportsStockPush: boolean;
}

export interface HttpRequest {
  readonly method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly url: string;
  readonly headers?: Readonly<Record<string, string>>;
  readonly query?: Readonly<Record<string, string | number | boolean | undefined>>;
  readonly body?: unknown;
  readonly timeoutMs?: number;
}

export interface HttpResponse<T = unknown> {
  readonly status: number;
  readonly headers: Readonly<Record<string, string>>;
  readonly body: T;
}

/** Injected transport: retries with backoff, per-channel rate limits, logs with secrets redacted. */
export interface HttpClient {
  request<T = unknown>(request: HttpRequest): Promise<HttpResponse<T>>;
}

export interface AdapterContext<C extends IntegratedChannel = IntegratedChannel> {
  readonly tenantId: TenantId;
  readonly credentialId: CredentialId;
  readonly channel: C;
  readonly externalAccountId: string;
  readonly config: ChannelConfig[C];
  readonly secrets: ChannelSecrets[C];
  readonly http: HttpClient;
  readonly signal?: AbortSignal;
}

export interface CredentialCheck {
  readonly ok: boolean;
  readonly message: string | null;
}

export interface FetchOrdersQuery {
  /** Orders created or changed in [updatedFrom, updatedTo) */
  readonly updatedFrom: IsoDateTime;
  readonly updatedTo: IsoDateTime;
  readonly cursor: string | null;
  readonly pageSize: number;
  /** where the previous pull's window ended (null: no pull yet); the window starts before it, re-reading an overlap */
  readonly pulledUntil?: IsoDateTime | null;
  /**
   * Only orders still open on the channel are wanted (the seller's "not taken" screen): a pull that also reads
   * lists which can only give cancelled, shipped or delivered orders (Hepsiburada) leaves them out
   */
  readonly openOnly?: boolean;
}

export interface FetchOrdersPage {
  readonly orders: readonly NormalizedOrder[];
  /** null on the last page */
  readonly nextCursor: string | null;
  /**
   * A list the pull can go on without (Hepsiburada's package, cancelled, shipped and delivered lists) could not
   * be read: the page has no orders and the pull goes on with `nextCursor`. The caller reads that list's window
   * again later; it never says the keys were refused (only the main list can)
   */
  readonly failed?: Error;
}

export interface FulfillmentLine {
  readonly externalLineId: string;
  readonly quantity: number;
}

export interface InvoiceReference {
  readonly number: string;
  readonly issuedAt: IsoDateTime;
  readonly url: string | null;
}

export interface ShipmentReference {
  readonly carrier: CarrierCode;
  readonly trackingNumber: string;
  readonly trackingUrl: string | null;
  readonly shippedAt: IsoDateTime;
  /**
   * How the warehouse shipped it: 'marketplace' means under the marketplace's own code, which its carrier's
   * scan reports, so a number must never be sent back over it (it may be stale after a carrier switch)
   */
  readonly shippingModel?: ShippingModel;
}

/** Status feedback to the channel, e.g. Trendyol Picking → Invoiced once packing is done. */
export type FulfillmentUpdate =
  | { readonly kind: 'picking'; readonly ref: ExternalOrderRef; readonly lines: readonly FulfillmentLine[] }
  | {
      readonly kind: 'invoiced';
      readonly ref: ExternalOrderRef;
      readonly lines: readonly FulfillmentLine[];
      readonly invoice: InvoiceReference;
    }
  | {
      readonly kind: 'shipped';
      readonly ref: ExternalOrderRef;
      readonly lines: readonly FulfillmentLine[];
      readonly shipment: ShipmentReference;
    }
  /** Could not be supplied after all (Trendyol "UnSupplied") */
  | {
      readonly kind: 'unsupplied';
      readonly ref: ExternalOrderRef;
      readonly lines: readonly FulfillmentLine[];
      readonly reason: string;
    };

export type FulfillmentUpdateResult =
  | { readonly outcome: 'applied'; readonly channelStatus: string | null }
  | { readonly outcome: 'skipped'; readonly reason: 'not_supported' | 'already_applied' };

export interface ShippingLabelRequest {
  readonly ref: ExternalOrderRef;
  /** Tracking number the channel already assigned (Trendyol cargoTrackingNumber), when known */
  readonly trackingNumber: string | null;
  /** the carrier the channel assigned to the package (carrier code), when known */
  readonly carrier?: string | null;
  /** Lines in the parcel; channels that create the package while issuing the label need them (Hepsiburada) */
  readonly lines: readonly FulfillmentLine[];
  readonly parcels: number;
  readonly desi: number;
  readonly format: LabelFormat;
  readonly paper: PaperSize;
}

/** The carrier and cargo code a channel issued for a parcel its contracted carrier takes. */
export interface ShipmentCode {
  readonly carrier: CarrierCode | null;
  /** printed on the warehouse's own label as Code 128 (n11 campaign code, Çiçeksepeti partialNumber, …) */
  readonly trackingNumber: string;
  /** a package the channel created while issuing the code, if any */
  readonly externalPackageId?: string | null;
}

export interface StockPushItem {
  /** the listing's key on the channel: merchant SKU (stock code) */
  readonly sku: string;
  /** the channel's barcode of the listing (Trendyol updates stock by barcode) */
  readonly barcode: string | null;
  /** Hepsiburada SKU, Shopify variant id, … */
  readonly channelProductId: string | null;
  /** satılabilir stok to publish on the channel, in listing units */
  readonly available: number;
}

/** A listing the channel refused; `key` is the SKU, barcode or channel product id it was sent under. */
export interface StockPushRejection {
  readonly key: string;
  readonly reason: string;
}

export interface StockPushResult {
  readonly accepted: number;
  readonly rejected: readonly StockPushRejection[];
  /** channels that process stock updates later hand back an id to ask about them (stockPushStatus) */
  readonly batchId?: string | null;
}

export interface StockPushStatus {
  /** false while the channel is still working on the batch */
  readonly done: boolean;
  readonly rejected: readonly StockPushRejection[];
}

export interface InboundWebhook {
  readonly headers: Readonly<Record<string, string>>;
  /** Raw bytes: signatures are computed over the exact body */
  readonly rawBody: Uint8Array;
}

export type WebhookEvent =
  | { readonly type: 'order_changed'; readonly ref: ExternalOrderRef }
  | { readonly type: 'credentials_revoked' };

/** One item the seller sells on a channel, as the channel names it. */
export interface ChannelListingItem {
  /** the key orders carry: merchant SKU (stock code), else the barcode */
  readonly channelSku: string;
  readonly channelProductId: string | null;
  readonly barcode: string | null;
  readonly title: string;
  readonly imageUrl: string | null;
  /** the brand the marketplace lists it under, where it says */
  readonly brand?: string | null;
  /** what the marketplace shows in stock, where it says */
  readonly stock?: number | null;
}

export interface FetchListingsQuery {
  readonly cursor: string | null;
  readonly pageSize: number;
}

export interface FetchListingsPage {
  readonly items: readonly ChannelListingItem[];
  /** null on the last page */
  readonly nextCursor: string | null;
}

export interface FetchFinanceQuery {
  /** money movements of [from, to), by the marketplace's own date of each */
  readonly from: IsoDateTime;
  readonly to: IsoDateTime;
  /** where the previous page left off (date windows and lists, as fetchOrders); null: from the start */
  readonly cursor: string | null;
  readonly pageSize: number;
  /** documents whose detail lines are stored already (a Trendyol cargo invoice's parcels are read once) */
  readonly knownDocuments?: readonly string[];
  /**
   * Documents stored before their detail lines came (a Trendyol cargo invoice not itemised yet: its raw says
   * `detailsPending`), from before `from`, as stored: read again after the windows, every sync until they come
   */
  readonly pendingDocuments?: readonly { readonly id: string; readonly raw: Readonly<Record<string, unknown>> }[];
}

export interface FetchFinancePage {
  readonly rows: readonly NormalizedFinanceRow[];
  /** null on the last page */
  readonly nextCursor: string | null;
  /**
   * A list the sync can go on without could not be read (as FetchOrdersPage.failed): the page has none of
   * its rows and the sync goes on with `nextCursor`. Only the first list's failure is thrown
   */
  readonly failed?: Error;
  /**
   * Records the marketplace will not give however the call is narrowed ("Payment (2026-10-12)": Hepsiburada's
   * rows of one type on one day in TRY and USD together): said on the seller's page, but the read's days are
   * not held back for them as for a failure, since asked again they answer the same
   */
  readonly gap?: string;
}

/** A marketplace item on the mapping screen: its product, or the product it most likely is. */
export interface ChannelProductView {
  readonly id: string;
  readonly credentialId: CredentialId;
  readonly channel: IntegratedChannel;
  readonly accountLabel: string;
  readonly channelSku: string;
  readonly channelProductId: string | null;
  readonly barcode: string | null;
  readonly title: string;
  readonly imageUrl: string | null;
  readonly brand: string | null;
  /** stock on the marketplace at the last read of its product list */
  readonly stock: number | null;
  /** api: from the channel's product list; order: met in an order that matched nothing */
  readonly source: 'api' | 'order';
  readonly unmatchedOrders: number;
  readonly lastSeenAt: IsoDateTime;
  readonly listing: { readonly id: string; readonly productId: ProductId; readonly sku: string; readonly name: string; readonly unitsPerListing: number } | null;
  /** an exact barcode or SKU match, when the item is not mapped yet */
  readonly suggestion: { readonly productId: ProductId; readonly sku: string; readonly name: string; readonly via: 'barcode' | 'sku'; readonly unitsPerListing: number } | null;
}

/**
 * An order still open on the marketplace that the warehouse does not have: placed before order
 * intake started, or one the seller had already started on. The seller may take it in.
 */
export interface OpenChannelOrder {
  readonly externalOrderId: string;
  readonly externalPackageId: string | null;
  readonly orderNumber: string;
  /** the marketplace's own word for its state ("Created", "Picking", "Invoiced") */
  readonly channelStatus: string;
  readonly state: ChannelOrderState;
  readonly orderedAt: IsoDateTime;
  readonly shipBy: IsoDateTime | null;
  readonly lines: readonly { readonly name: string; readonly quantity: number }[];
}

/** What the seller's stores show in stock per product: the first shipment to the warehouse starts from it. */
export interface MarketplaceStock {
  readonly lines: readonly {
    readonly productId: ProductId;
    readonly sku: string;
    readonly name: string;
    /** the highest count among the marketplaces that sell it (the same goods) */
    readonly qty: number;
    readonly sources: readonly { readonly channel: IntegratedChannel; readonly account: string; readonly qty: number }[];
  }[];
  /** accounts whose product list could not be read now (their last counts are used) */
  readonly problems: readonly { readonly account: string; readonly message: string }[];
}

/** Products made from marketplace items that matched nothing in the catalog. */
export interface ChannelProductsCreated {
  /** made and mapped to their item */
  readonly created: number;
  /** left for a person: a set, a stock code or barcode the catalog already has, … */
  readonly skipped: readonly { readonly channelSku: string; readonly title: string; readonly reason: string }[];
  /** orders that waited for these items and now go on */
  readonly releasedOrders: number;
}

export interface MarketplaceAdapter<C extends IntegratedChannel = IntegratedChannel> {
  readonly channel: C;
  readonly capabilities: AdapterCapabilities;

  /** Cheap authenticated call to validate a new or rotated credential. */
  verifyCredentials(ctx: AdapterContext<C>): Promise<CredentialCheck>;

  /** One page of orders changed in a time window, already normalised. */
  fetchOrders(ctx: AdapterContext<C>, query: FetchOrdersQuery): Promise<FetchOrdersPage>;

  /** Re-read one order (webhook follow-up, reconciliation). */
  fetchOrder(ctx: AdapterContext<C>, ref: ExternalOrderRef): Promise<NormalizedOrder | null>;

  /** Report fulfillment progress; kinds the channel does not know resolve as `skipped`. */
  pushFulfillment(ctx: AdapterContext<C>, update: FulfillmentUpdate): Promise<FulfillmentUpdateResult>;

  /** Contracted-carrier label (only when capabilities.providesShippingLabel). */
  getShippingLabel?(ctx: AdapterContext<C>, request: ShippingLabelRequest): Promise<ShippingLabel>;
  /**
   * For a channel that issues a cargo code for its contracted carrier but draws no label (n11, Çiçeksepeti,
   * Pazarama, İdefix, …): readies the parcel on the channel and returns the code; the warehouse prints its
   * own label with it. Called at label time and again after a failure, so it must be safe to repeat; a
   * channel that gave the code with the order may simply return it.
   */
  prepareShipment?(ctx: AdapterContext<C>, request: ShippingLabelRequest): Promise<ShipmentCode>;
  /** The seller's items on the channel, page by page, to map them to products before orders come. */
  fetchListings?(ctx: AdapterContext<C>, query: FetchListingsQuery): Promise<FetchListingsPage>;

  /** Sets the stock of listings; at most STOCK_PUSH_LIMITS[channel].batch items a call. */
  pushStock?(ctx: AdapterContext<C>, items: readonly StockPushItem[]): Promise<StockPushResult>;
  /** What became of a stock batch the channel processes later (Trendyol, Hepsiburada). */
  stockPushStatus?(ctx: AdapterContext<C>, batchId: string): Promise<StockPushStatus>;

  /**
   * The seller's money movements on the channel (sales, commissions, cargo, fees, payouts …), page by page,
   * normalised: Kârlılık reads them every night. Read-only and safe to repeat; a key without the finance
   * role answers AUTH_FAILED (orders are not affected).
   */
  fetchFinance?(ctx: AdapterContext<C>, query: FetchFinanceQuery): Promise<FetchFinancePage>;

  /** Verifies the signature before reading the payload. */
  parseWebhook?(ctx: AdapterContext<C>, request: InboundWebhook): Promise<readonly WebhookEvent[]>;
}

/** Strategy registry: services look the adapter up by channel instead of switching on it. */
export type MarketplaceAdapterRegistry = { readonly [C in IntegratedChannel]: MarketplaceAdapter<C> };

export const MARKETPLACE_ERROR_CODES = [
  'AUTH_FAILED',
  'RATE_LIMITED',
  'NOT_FOUND',
  'INVALID_STATE',
  'VALIDATION',
  'UNAVAILABLE',
  'UNSUPPORTED',
] as const;
export type MarketplaceErrorCode = (typeof MARKETPLACE_ERROR_CODES)[number];

/** What adapters throw; the BullMQ worker retries only retryable errors. */
export interface MarketplaceErrorInfo {
  readonly code: MarketplaceErrorCode;
  readonly channel: IntegratedChannel;
  readonly retryable: boolean;
  readonly retryAfterMs: number | null;
  readonly httpStatus: number | null;
  readonly message: string;
}
